'use client';

import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Download,
    File,
    Loader2,
    RotateCw,
    ZoomIn,
    ZoomOut,
} from 'lucide-react';
import {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { buildPublicUrl } from '@/lib/r2-urls';

// Lazy-load react-pdf and its CSS at runtime to avoid executing pdfjs
// during server prerender (some pdfjs internals use newer Promise APIs
// not available in all Node runtimes). We'll import them inside a
// useEffect so they're only loaded on the client.
const usePdfLoader = () => {
    const [pdfModules, setPdfModules] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const mod = await import('react-pdf');
                // load the CSS asynchronously
                await Promise.all([
                    import('react-pdf/dist/esm/Page/AnnotationLayer.css'),
                    import('react-pdf/dist/esm/Page/TextLayer.css'),
                ]);
                if (!mounted) return;
                // set workerSrc now that pdfjs is available
                try {
                    if (mod?.pdfjs?.GlobalWorkerOptions) {
                        // Use unpkg.com directly (CSP now allows it for script-src and connect-src)
                        mod.pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${mod.pdfjs.version}/build/pdf.worker.min.mjs`;
                    }
                } catch (_e) {
                    // best-effort
                }
                setPdfModules({
                    Document: mod.Document,
                    Page: mod.Page,
                    pdfjs: mod.pdfjs,
                });
            } catch (e) {
                // ignore; viewer will show error UI if needed
                console.warn('Failed to load pdf modules', e);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return pdfModules;
};

// Structured logging helper for client-side components
const structuredLog = (level, event, details = {}) => {
    try {
        const payload = {
            ts: new Date().toISOString(),
            level,
            service: 'brochure-viewer',
            event,
            ...details,
        };

        // Attach request id if the layout injected it on the window
        if (typeof window !== 'undefined' && window.__REQUEST_ID__) {
            payload.requestId = window.__REQUEST_ID__;
        }

        const out = JSON.stringify(payload);
        if (level === 'error') console.error(out);
        else if (level === 'warn') console.warn(out);
        else {
            // suppressed debug output
        }
    } catch (e) {
        // Fallback to console for unexpected serialization errors
        console.error('structuredLog error', e);
    }
};

// Debounce utility function
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Initial state for the viewer
const initialViewerState = {
    numPages: null,
    pageNumber: 1,
    scale: 1.0,
    loading: true,
    error: null,
    pdfWidth: null,
    pdfHeight: null,
    fitMode: 'auto',
    showZoomDropdown: false,
    showControls: true,
    rotation: 0,
    showMobileMenu: false,
    pageLoadingStates: {},
    estimatedPageDimensions: { width: 612, height: 792 },
};

// Stable module-level constant — avoids react-pdf "prop changed but equal"
// warnings caused by reference inequality across Strict Mode double-mounts
const PDF_OPTIONS = Object.freeze({
    cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
    cMapPacked: true,
    enableXfa: true,
    useWorkerFetch: true,
    disableAutoFetch: false,
    disableStream: false,
    maxImageSize: -1,
});

// Reducer for viewer state management
const viewerReducer = (state, action) => {
    switch (action.type) {
        case 'SET_NUM_PAGES':
            return { ...state, numPages: action.payload, loading: false };
        case 'SET_PAGE_NUMBER':
            return { ...state, pageNumber: action.payload };
        case 'SET_SCALE':
            return { ...state, scale: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ERROR':
            return { ...state, error: action.payload, loading: false };
        case 'SET_PDF_DIMENSIONS':
            return {
                ...state,
                pdfWidth: action.payload.width,
                pdfHeight: action.payload.height,
                estimatedPageDimensions: action.payload,
            };
        case 'SET_FIT_MODE':
            return { ...state, fitMode: action.payload };
        case 'SET_SHOW_ZOOM_DROPDOWN':
            return { ...state, showZoomDropdown: action.payload };
        case 'SET_SHOW_CONTROLS':
            return { ...state, showControls: action.payload };
        case 'SET_ROTATION':
            return { ...state, rotation: action.payload };
        case 'SET_SHOW_MOBILE_MENU':
            return { ...state, showMobileMenu: action.payload };
        case 'SET_PAGE_LOADING':
            return {
                ...state,
                pageLoadingStates: {
                    ...state.pageLoadingStates,
                    [action.payload.page]: action.payload.loading,
                },
            };
        case 'CLEAR_PAGE_LOADING_STATES':
            return { ...state, pageLoadingStates: {} };
        default:
            return state;
    }
};

export default function BrochureViewer({ pdfPath }) {
    const [state, dispatch] = useReducer(viewerReducer, initialViewerState);
    const pdfModules = usePdfLoader();
    const [controlsTimeout, _setControlsTimeout] = useState(null);
    const [preloadedPages, setPreloadedPages] = useState(new Set());

    const containerRef = useRef(null);
    const documentRef = useRef(null);
    const cleanupRef = useRef([]);

    const [pdfUrl, setPdfUrl] = useState(() =>
        pdfPath ? buildPublicUrl(pdfPath) : '',
    );
    const [pdfFallbackApplied, setPdfFallbackApplied] = useState(false);

    useEffect(() => {
        if (pdfPath) {
            // Try CDN URL directly first (CORS headers are now set on the bucket)
            const cdnUrl = buildPublicUrl(pdfPath);
            setPdfUrl(cdnUrl);
        } else {
            setPdfUrl('');
        }
        setPdfFallbackApplied(false);
    }, [pdfPath]);

    const memoizedFile = useMemo(() => ({ url: pdfUrl }), [pdfUrl]);

    // Cleanup function for component unmount
    const cleanup = useCallback(() => {
        // Clear timeouts
        if (controlsTimeout) {
            clearTimeout(controlsTimeout);
        }

        // Cleanup document references
        if (documentRef.current) {
            try {
                documentRef.current.destroy?.();
            } catch (error) {
                structuredLog('warn', 'destroy_document_failed', {
                    message: String(error),
                });
            }
            documentRef.current = null;
        }

        // Clear preloaded pages
        setPreloadedPages(new Set());

        // Clear loading states
        dispatch({ type: 'CLEAR_PAGE_LOADING_STATES' });

        // Restore body scroll
        document.body.style.overflow = 'unset';

        // Run any additional cleanup functions
        cleanupRef.current.forEach((cleanupFn) => {
            try {
                cleanupFn();
            } catch (error) {
                structuredLog('warn', 'cleanup_fn_error', {
                    message: String(error),
                });
            }
        });
        cleanupRef.current = [];
    }, [controlsTimeout]);

    // Main cleanup effect
    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    // Enhanced error handling for PDF loading
    const handleLoadError = useCallback(
        (error) => {
            // Don't log AbortException errors which are normal during rapid navigation
            if (error.name === 'AbortException') return;

            // Don't log TextLayer warnings
            if (error.message?.includes('TextLayer task cancelled')) return;

            if (!pdfFallbackApplied && pdfPath) {
                // First fallback: try the proxy endpoint
                const proxyUrl = `/api/proxy-pdf?file=${encodeURIComponent(pdfPath)}`;
                if (pdfUrl !== proxyUrl) {
                    setPdfUrl(proxyUrl);
                    setPdfFallbackApplied(true);
                    return;
                }
            }

            if (pdfFallbackApplied && pdfPath && pdfUrl !== pdfPath) {
                setPdfUrl(pdfPath);
                return;
            }

            structuredLog('error', 'pdf_load_error', {
                message: String(error),
            });
            dispatch({
                type: 'SET_ERROR',
                payload: 'Failed to load PDF document. Please try again later.',
            });
        },
        [pdfPath, pdfFallbackApplied, pdfUrl],
    );

    // Safe page loading state management
    const setPageLoading = useCallback((pageNum, isLoading) => {
        dispatch({
            type: 'SET_PAGE_LOADING',
            payload: { page: pageNum, loading: isLoading },
        });
    }, []);

    // Optimized preloading with proper cleanup
    // capture the specific state fields we need so the callback can
    // depend on those primitives instead of the whole `state` object
    const currentPageNumber = state.pageNumber;
    const currentNumPages = state.numPages;

    const preloadPages = useCallback(() => {
        if (!documentRef.current || !currentNumPages || !currentPageNumber)
            return;

        const pagesToPreload = [];
        const pageNumber = currentPageNumber;
        const numPages = currentNumPages;

        // Preload adjacent pages
        if (pageNumber < numPages) pagesToPreload.push(pageNumber + 1);
        if (pageNumber > 1) pagesToPreload.push(pageNumber - 1);
        if (pageNumber + 2 <= numPages) pagesToPreload.push(pageNumber + 2);

        pagesToPreload.forEach((pageNum) => {
            if (!preloadedPages.has(pageNum)) {
                const preloadPromise = documentRef.current
                    .getPage(pageNum)
                    .then(() => {
                        setPreloadedPages(
                            (prev) => new Set([...prev, pageNum]),
                        );
                    })
                    .catch(() => {
                        // Silently fail for preloading
                    });

                // Store cleanup function
                cleanupRef.current.push(() => {
                    if (preloadPromise.cancel) {
                        preloadPromise.cancel();
                    }
                });
            }
        });
    }, [currentPageNumber, currentNumPages, preloadedPages]);

    // Debounced preloading effect
    useEffect(() => {
        const debouncedPreload = debounce(preloadPages, 500);
        debouncedPreload();

        return () => {
            // Cleanup is handled by the main cleanup function
        };
    }, [preloadPages]);

    // Enhanced keyboard shortcuts with proper cleanup
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.target.tagName === 'INPUT') return;

            switch (event.key) {
                case 'Escape':
                    // if (state.isFullScreen) {
                    //     event.preventDefault();
                    //     dispatch({ type: 'SET_FULLSCREEN', payload: false });
                    // }
                    break;
                case 'ArrowLeft': {
                    event.preventDefault();
                    const prevPage = Math.max(state.pageNumber - 1, 1);
                    if (prevPage !== state.pageNumber) {
                        setPageLoading(prevPage, true);
                        dispatch({
                            type: 'SET_PAGE_NUMBER',
                            payload: prevPage,
                        });
                    }
                    break;
                }
                case 'ArrowRight': {
                    event.preventDefault();
                    const nextPageNum = state.numPages
                        ? Math.min(state.pageNumber + 1, state.numPages)
                        : state.pageNumber;
                    if (nextPageNum !== state.pageNumber) {
                        setPageLoading(nextPageNum, true);
                        dispatch({
                            type: 'SET_PAGE_NUMBER',
                            payload: nextPageNum,
                        });
                    }
                    break;
                }
                case '+':
                case '=':
                    event.preventDefault();
                    dispatch({ type: 'SET_FIT_MODE', payload: 'manual' });
                    dispatch({
                        type: 'SET_SCALE',
                        payload: Math.min(state.scale * 1.2, 4.0),
                    });
                    break;
                case '-':
                    event.preventDefault();
                    dispatch({ type: 'SET_FIT_MODE', payload: 'manual' });
                    dispatch({
                        type: 'SET_SCALE',
                        payload: Math.max(state.scale / 1.2, 0.25),
                    });
                    break;
                case '0':
                    event.preventDefault();
                    dispatch({ type: 'SET_FIT_MODE', payload: 'actual' });
                    break;
                case 'r':
                case 'R':
                    if (!event.ctrlKey && !event.metaKey) {
                        event.preventDefault();
                        dispatch({
                            type: 'SET_ROTATION',
                            payload: (state.rotation + 90) % 360,
                        });
                    }
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        const cleanup = () => {
            document.removeEventListener('keydown', handleKeyDown);
        };

        cleanupRef.current.push(cleanup);
        return cleanup;
    }, [
        state.pageNumber,
        state.numPages,
        state.scale,
        state.rotation,
        setPageLoading,
    ]);

    // Enhanced click outside detection with proper cleanup
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                state.showZoomDropdown &&
                !event.target.closest('.zoom-dropdown-container')
            ) {
                dispatch({ type: 'SET_SHOW_ZOOM_DROPDOWN', payload: false });
            }
            if (
                state.showMobileMenu &&
                !event.target.closest('.mobile-menu-container')
            ) {
                dispatch({ type: 'SET_SHOW_MOBILE_MENU', payload: false });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        const cleanup = () =>
            document.removeEventListener('mousedown', handleClickOutside);
        cleanupRef.current.push(cleanup);

        return cleanup;
    }, [state.showZoomDropdown, state.showMobileMenu]);

    // Optimized scale calculation with debouncing
    const updateScale = useCallback(() => {
        // Fall back to known page dimensions (US Letter: 612×792) when actual
        // PDF dimensions haven't been measured yet — this lets us calculate
        // the correct auto-fit scale before the first render, eliminating CLS
        // without forcing a React re-render / remount of the <Page> component.
        const effectiveWidth = state.pdfWidth || 612;
        const effectiveHeight = state.pdfHeight || 792;

        if (containerRef.current) {
            const container = containerRef.current;
            const availableWidth = container.clientWidth - 32;
            const availableHeight = container.clientHeight - 32;

            let newScale = state.scale;

            // Only update scale for non-manual fit modes
            if (state.fitMode !== 'manual') {
                switch (state.fitMode) {
                    case 'width':
                        newScale = availableWidth / effectiveWidth;
                        break;
                    case 'height':
                        newScale = availableHeight / effectiveHeight;
                        break;
                    case 'page': {
                        const scaleWidth = availableWidth / effectiveWidth;
                        const scaleHeight = availableHeight / effectiveHeight;
                        newScale = Math.min(scaleWidth, scaleHeight);
                        break;
                    }
                    case 'actual':
                        newScale = 1.0;
                        break;
                    default:
                        newScale = Math.min(
                            availableWidth / effectiveWidth,
                            1.2,
                        );
                        break;
                }

                // Clamp the scale to reasonable bounds (allow below 125%)
                newScale = Math.min(Math.max(newScale, 0.25), 4.0);

                // Only update if the scale actually changed
                if (Math.abs(newScale - state.scale) > 0.01) {
                    dispatch({ type: 'SET_SCALE', payload: newScale });
                }
            }
        }
    }, [state.pdfWidth, state.pdfHeight, state.fitMode, state.scale]);

    // Debounced resize handler
    const debouncedUpdateScale = useMemo(
        () => debounce(updateScale, 100),
        [updateScale],
    );

    useEffect(() => {
        // Only update scale for non-manual fit modes
        if (state.fitMode !== 'manual') {
            updateScale();
        }

        window.addEventListener('resize', debouncedUpdateScale);

        const cleanup = () =>
            window.removeEventListener('resize', debouncedUpdateScale);
        cleanupRef.current.push(cleanup);

        return cleanup;
    }, [state.fitMode, debouncedUpdateScale, updateScale]);

    // Enhanced PDF document loading with proper cleanup
    const onDocumentLoadSuccess = useCallback(
        ({ numPages }) => {
            dispatch({ type: 'SET_NUM_PAGES', payload: numPages });

            // Store low-level pdfjs Document reference for preloading with proper cleanup
            // Use the pdfjs provided by the lazily-loaded modules and guard when not available.
            if (!pdfModules?.pdfjs) return;

            pdfModules.pdfjs
                .getDocument({ ...memoizedFile, ...PDF_OPTIONS })
                .promise.then((doc) => {
                    documentRef.current = doc;

                    // Store cleanup function for document
                    cleanupRef.current.push(() => {
                        if (doc?.destroy) {
                            doc.destroy();
                        }
                    });
                })
                .catch((error) => {
                    if (error.name !== 'AbortException') {
                        structuredLog('warn', 'store_document_ref_failed', {
                            message: String(error),
                        });
                    }
                });
        },
        [memoizedFile, pdfModules],
    );

    const onPageLoadSuccess = useCallback(
        ({ width, height }) => {
            if (!state.pdfWidth || !state.pdfHeight) {
                dispatch({
                    type: 'SET_PDF_DIMENSIONS',
                    payload: { width, height },
                });
            }

            // Mark this page as loaded
            setPageLoading(state.pageNumber, false);
        },
        [state.pdfWidth, state.pdfHeight, state.pageNumber, setPageLoading],
    );

    // Optimized navigation functions with proper state management
    const zoomIn = useCallback(() => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'manual' });
        dispatch({
            type: 'SET_SCALE',
            payload: Math.min(state.scale * 1.2, 4.0),
        });
    }, [state.scale]);

    const zoomOut = useCallback(() => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'manual' });
        dispatch({
            type: 'SET_SCALE',
            payload: Math.max(state.scale / 1.2, 0.25),
        });
    }, [state.scale]);

    const setFitToWidth = useCallback(() => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'width' });
        dispatch({ type: 'SET_SHOW_ZOOM_DROPDOWN', payload: false });
    }, []);

    const setFitToHeight = useCallback(() => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'height' });
        dispatch({ type: 'SET_SHOW_ZOOM_DROPDOWN', payload: false });
    }, []);

    const setFitToPage = useCallback(() => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'page' });
        dispatch({ type: 'SET_SHOW_ZOOM_DROPDOWN', payload: false });
    }, []);

    const setActualSize = useCallback(() => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'actual' });
        dispatch({ type: 'SET_SHOW_ZOOM_DROPDOWN', payload: false });
    }, []);

    const setCustomZoom = useCallback((zoomLevel) => {
        dispatch({ type: 'SET_FIT_MODE', payload: 'manual' });
        dispatch({ type: 'SET_SCALE', payload: zoomLevel / 100 });
        dispatch({ type: 'SET_SHOW_ZOOM_DROPDOWN', payload: false });
    }, []);

    const rotateClockwise = useCallback(() => {
        dispatch({
            type: 'SET_ROTATION',
            payload: (state.rotation + 90) % 360,
        });
    }, [state.rotation]);

    const previousPage = useCallback(() => {
        const newPage = Math.max(state.pageNumber - 1, 1);
        if (newPage !== state.pageNumber) {
            setPageLoading(newPage, true);
            dispatch({ type: 'SET_PAGE_NUMBER', payload: newPage });
        }
    }, [state.pageNumber, setPageLoading]);

    const nextPage = useCallback(() => {
        const newPage = state.numPages
            ? Math.min(state.pageNumber + 1, state.numPages)
            : state.pageNumber;
        if (newPage !== state.pageNumber) {
            setPageLoading(newPage, true);
            dispatch({ type: 'SET_PAGE_NUMBER', payload: newPage });
        }
    }, [state.pageNumber, state.numPages, setPageLoading]);

    const goToPage = useCallback(
        (page) => {
            const pageNum = Math.max(1, Math.min(page, state.numPages || 1));
            if (pageNum !== state.pageNumber) {
                setPageLoading(pageNum, true);
                dispatch({ type: 'SET_PAGE_NUMBER', payload: pageNum });
            }
        },
        [state.pageNumber, state.numPages, setPageLoading],
    );

    const predefinedZooms = [25, 50, 75, 100, 125, 150, 200, 250, 300, 400];

    const ZoomDropdown = ({ isFullScreenMode = false }) => {
        const baseClasses = 'px-3 py-2 text-left transition-colors text-sm';
        const buttonClass = isFullScreenMode
            ? `${baseClasses} hover:bg-white/10 text-white`
            : `${baseClasses} hover:bg-gray-100 text-gray-700`;

        return (
            <div
                className={`absolute right-0 top-full mt-2 rounded-xl shadow-lg border backdrop-blur-sm z-50 min-w-[140px] ${
                    isFullScreenMode
                        ? 'bg-black/80 border-white/20'
                        : 'bg-white/90 border-gray-200'
                }`}
            >
                <div className="p-1">
                    <button
                        type="button"
                        onClick={setFitToPage}
                        className={`${buttonClass} w-full rounded-lg`}
                    >
                        Fit to Page
                    </button>
                    <button
                        type="button"
                        onClick={setFitToWidth}
                        className={`${buttonClass} w-full rounded-lg`}
                    >
                        Fit to Width
                    </button>
                    <button
                        type="button"
                        onClick={setFitToHeight}
                        className={`${buttonClass} w-full rounded-lg`}
                    >
                        Fit to Height
                    </button>
                    <button
                        type="button"
                        onClick={setActualSize}
                        className={`${buttonClass} w-full rounded-lg`}
                    >
                        Actual Size
                    </button>
                    <div
                        className={`border-t ${
                            isFullScreenMode
                                ? 'border-white/20'
                                : 'border-gray-200'
                        } my-1`}
                    ></div>
                    {predefinedZooms.map((zoom) => (
                        <button
                            type="button"
                            key={zoom}
                            onClick={() => setCustomZoom(zoom)}
                            className={`${buttonClass} w-full rounded-lg ${
                                Math.round(state.scale * 100) === zoom
                                    ? isFullScreenMode
                                        ? 'bg-white/10'
                                        : 'bg-white'
                                    : ''
                            }`}
                        >
                            {zoom}%
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const PageInput = ({ isFullScreenMode = false }) => {
        // capture the primitives we need from outer `state` so the
        // hook dependencies are stable and explicit
        const pageNumber = state.pageNumber;
        const [inputValue, setInputValue] = useState(
            state.pageNumber.toString(),
        );
        const isCurrentPageLoading = state.pageLoadingStates[pageNumber];

        const handleSubmit = (e) => {
            e.preventDefault();
            const page = parseInt(inputValue, 10);
            if (!Number.isNaN(page)) {
                goToPage(page);
            }
        };

        return (
            <form
                onSubmit={handleSubmit}
                className="flex items-center space-x-2"
            >
                <div className="relative">
                    <label htmlFor="brochure-page-input" className="sr-only">
                        Page number
                    </label>
                    <input
                        id="brochure-page-input"
                        key={state.pageNumber}
                        type="number"
                        min="1"
                        max={state.numPages || 1}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className={`w-16 px-2 py-1 rounded-lg border text-center text-sm ${
                            isFullScreenMode
                                ? 'bg-white/10 border-white/20 text-white placeholder-white/50'
                                : 'bg-white border-gray-200 text-gray-700'
                        }`}
                    />
                    {isCurrentPageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2
                                className={`w-3 h-3 animate-spin ${
                                    isFullScreenMode
                                        ? 'text-white'
                                        : 'text-brand'
                                }`}
                            />
                        </div>
                    )}
                </div>
                <span
                    className={`text-sm ${isFullScreenMode ? 'text-white/80' : 'text-gray-600'}`}
                >
                    of {state.numPages || '...'}
                </span>
            </form>
        );
    };

    const ToolbarButton = ({
        onClick,
        disabled,
        children,
        className = '',
        'aria-label': ariaLabel,
        isFullScreenMode = false,
        showLoading = false,
    }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || showLoading}
            className={`p-2 rounded-lg transition-all duration-200 relative ${
                isFullScreenMode
                    ? 'bg-white/10 text-white hover:bg-white/20 disabled:opacity-50'
                    : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 shadow-sm hover:shadow-md'
            } disabled:cursor-not-allowed ${className}`}
            aria-label={ariaLabel}
        >
            {showLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
                children
            )}
        </button>
    );

    const ActionButton = ({
        onClick,
        children,
        className = '',
        'aria-label': ariaLabel,
        isFullScreenMode = false,
    }) => (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isFullScreenMode
                    ? 'bg-brand text-white hover:bg-brand-dark'
                    : 'bg-brand text-white hover:bg-brand-dark shadow-lg hover:shadow-xl'
            } ${className}`}
            aria-label={ariaLabel}
        >
            {children}
        </button>
    );

    const PagePlaceholder = ({
        pageNum,
        scale,
        rotation,
        className = '',
        isFullScreenMode = false,
    }) => {
        const dimensions = state.estimatedPageDimensions;
        const isCurrentPageLoading = state.pageLoadingStates[pageNum];

        return (
            <div
                className={`flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-200 ${className}`}
                style={{
                    width: dimensions.width * scale,
                    height: dimensions.height * scale,
                    transform: `rotate(${rotation}deg)`,
                    transition: 'transform 0.3s ease',
                }}
            >
                <div className="text-center p-8">
                    {isCurrentPageLoading ? (
                        <>
                            <Loader2
                                className={`w-8 h-8 animate-spin mx-auto mb-3 ${
                                    isFullScreenMode
                                        ? 'text-white'
                                        : 'text-brand'
                                }`}
                            />
                            <p
                                className={`text-sm font-medium ${
                                    isFullScreenMode
                                        ? 'text-white/80'
                                        : 'text-gray-600'
                                }`}
                            >
                                Loading Page {pageNum}...
                            </p>
                        </>
                    ) : (
                        <>
                            <File
                                className={`w-12 h-12 mx-auto mb-3 ${
                                    isFullScreenMode
                                        ? 'text-white/70'
                                        : 'text-gray-500'
                                }`}
                            />
                            <p
                                className={`text-sm ${
                                    isFullScreenMode
                                        ? 'text-white/80'
                                        : 'text-gray-600'
                                }`}
                            >
                                Page {pageNum}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const downloadBrochure = useCallback(() => {
        if (!pdfUrl) return;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'trenchmate-brochure.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [pdfUrl]);

    return (
        <section className="min-h-screen bg-white py-8">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        Trenchmate Brochure
                    </h2>
                    <p className="text-gray-600">
                        Use keyboard shortcuts for better navigation
                    </p>
                </div>

                {/* Desktop Controls */}
                <div className="hidden lg:flex justify-between items-center mb-6 bg-gray-100 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center space-x-4">
                        <ToolbarButton
                            onClick={previousPage}
                            disabled={state.pageNumber <= 1}
                            aria-label="Previous page"
                            showLoading={
                                state.pageLoadingStates[state.pageNumber - 1]
                            }
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </ToolbarButton>

                        <PageInput />

                        <ToolbarButton
                            onClick={nextPage}
                            disabled={
                                state.numPages !== null &&
                                state.pageNumber >= state.numPages
                            }
                            aria-label="Next page"
                            showLoading={
                                state.pageLoadingStates[state.pageNumber + 1]
                            }
                        >
                            <ChevronRight className="w-5 h-5" />
                        </ToolbarButton>
                    </div>

                    <div className="flex items-center space-x-4">
                        <ToolbarButton onClick={zoomOut} aria-label="Zoom out">
                            <ZoomOut className="w-5 h-5" />
                        </ToolbarButton>

                        <div className="zoom-dropdown-container relative">
                            <ToolbarButton
                                onClick={() =>
                                    dispatch({
                                        type: 'SET_SHOW_ZOOM_DROPDOWN',
                                        payload: !state.showZoomDropdown,
                                    })
                                }
                                aria-label="Zoom options"
                                className="min-w-20 flex items-center"
                            >
                                <span className="font-medium">
                                    {Math.round(state.scale * 100)}%
                                </span>
                                <ChevronDown className="w-4 h-4 ml-1" />
                            </ToolbarButton>
                            {state.showZoomDropdown && <ZoomDropdown />}
                        </div>

                        <ToolbarButton onClick={zoomIn} aria-label="Zoom in">
                            <ZoomIn className="w-5 h-5" />
                        </ToolbarButton>

                        <ToolbarButton
                            onClick={rotateClockwise}
                            aria-label="Rotate clockwise"
                        >
                            <RotateCw className="w-5 h-5" />
                        </ToolbarButton>

                        <ActionButton
                            onClick={downloadBrochure}
                            aria-label="Download brochure"
                        >
                            <Download className="w-5 h-5" />
                            Download
                        </ActionButton>
                    </div>
                </div>

                {/* Mobile Controls */}
                <div className="lg:hidden mb-6">
                    <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center space-x-3">
                                {' '}
                                <ToolbarButton
                                    onClick={previousPage}
                                    disabled={state.pageNumber <= 1}
                                    aria-label="Previous page"
                                    showLoading={
                                        state.pageLoadingStates[
                                            state.pageNumber - 1
                                        ]
                                    }
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </ToolbarButton>
                                <PageInput />
                                <ToolbarButton
                                    onClick={nextPage}
                                    disabled={
                                        state.numPages !== null &&
                                        state.pageNumber >= state.numPages
                                    }
                                    aria-label="Next page"
                                    showLoading={
                                        state.pageLoadingStates[
                                            state.pageNumber + 1
                                        ]
                                    }
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </ToolbarButton>
                            </div>

                            <div className="mobile-menu-container relative">
                                <ToolbarButton
                                    onClick={() =>
                                        dispatch({
                                            type: 'SET_SHOW_MOBILE_MENU',
                                            payload: !state.showMobileMenu,
                                        })
                                    }
                                    aria-label="More options"
                                >
                                    <ChevronDown className="w-5 h-5" />
                                </ToolbarButton>

                                {state.showMobileMenu && (
                                    <div className="absolute right-0 top-full mt-2 rounded-xl shadow-lg border bg-white/90 backdrop-blur-sm border-gray-200 z-50 min-w-[200px]">
                                        <div className="p-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    zoomOut();
                                                    dispatch({
                                                        type: 'SET_SHOW_MOBILE_MENU',
                                                        payload: false,
                                                    });
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg text-gray-700"
                                            >
                                                <ZoomOut className="w-5 h-5" />
                                                <span>Zoom Out</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    zoomIn();
                                                    dispatch({
                                                        type: 'SET_SHOW_MOBILE_MENU',
                                                        payload: false,
                                                    });
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg text-gray-700"
                                            >
                                                <ZoomIn className="w-5 h-5" />
                                                <span>Zoom In</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    rotateClockwise();
                                                    dispatch({
                                                        type: 'SET_SHOW_MOBILE_MENU',
                                                        payload: false,
                                                    });
                                                }}
                                                className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg text-gray-700"
                                            >
                                                <RotateCw className="w-5 h-5" />
                                                <span>Rotate</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <ActionButton
                            onClick={downloadBrochure}
                            aria-label="Download brochure"
                            className="w-full justify-center"
                        >
                            <Download className="w-5 h-5" />
                            Download PDF
                        </ActionButton>
                    </div>
                </div>

                {/* PDF Viewer */}
                <div
                    ref={containerRef}
                    className="bg-gray-100 rounded-2xl shadow-lg overflow-hidden min-h-[80vh] relative"
                >
                    {' '}
                    {state.loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
                            <div className="text-center">
                                <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
                                <p className="text-gray-600 font-medium">
                                    Loading PDF...
                                </p>
                            </div>
                        </div>
                    )}
                    {state.error ? (
                        <div className="flex flex-col items-center justify-center text-center p-8 h-full min-h-[60vh]">
                            <File className="w-16 h-16 text-brand mb-4" />
                            <h3 className="text-xl font-semibold mb-2 text-gray-900">
                                Error Loading Brochure
                            </h3>
                            <p className="text-gray-600 mb-6">{state.error}</p>
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="bg-brand text-white px-6 py-3 rounded-lg hover:bg-brand-dark transition-all duration-200 font-medium cursor-pointer shadow-sm hover:shadow-md active:scale-95"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : (
                        <div className="flex justify-center p-8">
                            <div
                                className="transition-transform duration-300 ease-in-out"
                                style={{
                                    transform: `rotate(${state.rotation}deg)`,
                                }}
                            >
                                {pdfModules ? (
                                    <pdfModules.Document
                                        file={memoizedFile}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        onLoadError={handleLoadError}
                                        loading={
                                            <PagePlaceholder
                                                pageNum={state.pageNumber}
                                                scale={state.scale}
                                                rotation={0}
                                                className="shadow-xl rounded-lg overflow-hidden border border-gray-200"
                                            />
                                        }
                                        className="max-w-full flex items-center"
                                        options={PDF_OPTIONS}
                                    >
                                        <pdfModules.Page
                                            key={`normal_page_${state.pageNumber}_${state.rotation}_${state.scale}`}
                                            pageNumber={state.pageNumber}
                                            scale={state.scale}
                                            renderTextLayer={true}
                                            renderAnnotationLayer={true}
                                            className="shadow-xl rounded-lg overflow-hidden border border-gray-200"
                                            onLoadSuccess={onPageLoadSuccess}
                                            onLoadStart={() =>
                                                setPageLoading(
                                                    state.pageNumber,
                                                    true,
                                                )
                                            }
                                            onRenderError={(error) => {
                                                setPageLoading(
                                                    state.pageNumber,
                                                    false,
                                                );
                                                if (
                                                    error.name !==
                                                    'AbortException'
                                                ) {
                                                    structuredLog(
                                                        'error',
                                                        'page_render_error',
                                                        {
                                                            message:
                                                                String(error),
                                                        },
                                                    );
                                                }
                                            }}
                                            loading={
                                                <PagePlaceholder
                                                    pageNum={state.pageNumber}
                                                    scale={state.scale}
                                                    rotation={0}
                                                    className="shadow-xl rounded-lg overflow-hidden border border-gray-200"
                                                />
                                            }
                                        />
                                    </pdfModules.Document>
                                ) : (
                                    <PagePlaceholder
                                        pageNum={state.pageNumber}
                                        scale={state.scale}
                                        rotation={0}
                                        className="shadow-xl rounded-lg overflow-hidden border border-gray-200"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Keyboard Shortcuts Help */}
                <div className="mt-8 bg-white/50 backdrop-blur-sm rounded-xl p-4 text-center">
                    <p className="text-sm text-gray-600 mb-2">
                        <strong>Keyboard Shortcuts:</strong>
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                        <span>
                            <kbd className="bg-gray-200 px-2 py-1 rounded">
                                ←→
                            </kbd>{' '}
                            Navigate pages
                        </span>
                        <span>
                            <kbd className="bg-gray-200 px-2 py-1 rounded">
                                +/-
                            </kbd>{' '}
                            Zoom
                        </span>
                        <span>
                            <kbd className="bg-gray-200 px-2 py-1 rounded">
                                R
                            </kbd>{' '}
                            Rotate
                        </span>
                        <span>
                            <kbd className="bg-gray-200 px-2 py-1 rounded">
                                0
                            </kbd>{' '}
                            Actual size
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}
